import { JepCookiesService } from '@jepto/shared/services/jep-cookies/jep-cookies.service';
import { JepCurrentAccountService } from '@jepto/shared/services/jep-current-account/jep-current-account.service';
import { JepRequestService } from '@jepto/shared/services/jep-request/jep-request.service';
import { ANALYTICS_DATA_REQUESTER_RESOLVER } from '@framework/analytics/services/analytics-data-requester.service';
import { resolveEncodedReportIds } from '@framework/analytics/utils/report-id-encoding.util';
import { AnalyticsJepRequestAdapter } from './analytics-jep-request.adapter';
import { RequestService } from '../services/request.service';

/**
 * Host DI for framework services on the public analytics viewer.
 * Table paging/sort uses TableCardInteractionService → TablePreviewDataLoader →
 * JepRequestService; that token is bound to AnalyticsJepRequestAdapter (IAM RequestService).
 * Cookie / account stubs remain for other framework inject sites.
 */
function accountIdFromCurrentReportPath(): string {
	if (typeof window === 'undefined') {
		return '';
	}
	const pathSegment = window.location.pathname
		.split('/')
		.filter(Boolean)[0]
		?.trim();
	const queryReportId = new URLSearchParams(window.location.search).get(
		'reportId'
	);
	return resolveEncodedReportIds(pathSegment, queryReportId)?.accountId ?? '';
}

export const analyticsFrameworkDiProviders = [
	{
		provide: JepRequestService,
		useClass: AnalyticsJepRequestAdapter,
	},
	{
		provide: JepCurrentAccountService,
		useValue: {
			getJWTDetails: () => null,
			getAccountId: () => accountIdFromCurrentReportPath(),
			getCognitoUserId: () => '',
			getUserId: () => '',
			getJWTUserId: () => '',
		} satisfies Pick<
			JepCurrentAccountService,
			| 'getJWTDetails'
			| 'getAccountId'
			| 'getCognitoUserId'
			| 'getUserId'
			| 'getJWTUserId'
		>,
	},
	{
		provide: JepCookiesService,
		useValue: {
			getCookie: () => ({ idToken: '', accessToken: '' }),
			setCookie: () => {},
			deleteCookie: () => {},
			getCookieDetails: () => '',
			deleteCookieDetails: () => {},
			getAll: () => ({}),
		} satisfies Pick<
			JepCookiesService,
			| 'getCookie'
			| 'setCookie'
			| 'deleteCookie'
			| 'getCookieDetails'
			| 'deleteCookieDetails'
			| 'getAll'
		>,
	},
	{
		provide: ANALYTICS_DATA_REQUESTER_RESOLVER,
		useFactory: (requestService: RequestService) => {
			return async (): Promise<string> => {
				const cached = requestService.getCachedIdentityId();
				const identityId = cached?.trim()
					? cached
					: await requestService.getIdentityId();
				const trimmed = identityId.trim();
				const colonIndex = trimmed.indexOf(':');
				return colonIndex === -1
					? trimmed
					: trimmed.slice(colonIndex + 1).trim();
			};
		},
		deps: [RequestService],
	},
];
